import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ICONS = ['🏖️','⛱️','🪑','🛏️','🍔','🥤','🍺','☕','🧃','🥪','🍦','🌮','🍗','🥗','🍰','🧁','🫖','🍹','🥂'];

const CATEGORIES = [
  { value: 'beach', label: 'Plage' },
  { value: 'bar', label: 'Bar' },
  { value: 'restaurant', label: 'Restaurant' },
];

function ProductsPage({ apiUrl, auth }) {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', icon: '🏖️', categories: ['beach'] });

  const toggleCategory = (value) => {
    setForm(prev => {
      const has = prev.categories.includes(value);
      const categories = has ? prev.categories.filter(c => c !== value) : [...prev.categories, value];
      return { ...prev, categories };
    });
  };

  const productCategories = (product) => {
    if (Array.isArray(product.categories) && product.categories.length) return product.categories;
    return product.category ? [product.category] : ['beach'];
  };

  const headers = { Authorization: `Bearer ${auth.token}` };

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${apiUrl}/products`, { headers });
      setProducts(res.data);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const openAdd = () => {
    setEditProduct(null);
    setForm({ name: '', price: '', icon: '🏖️', categories: ['beach'] });
    setShowModal(true);
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setForm({ name: product.name, price: String(product.price), icon: product.icon, categories: productCategories(product) });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return;
    const categories = form.categories.length ? form.categories : ['beach'];
    try {
      if (editProduct) {
        await axios.put(`${apiUrl}/products/${editProduct.id}`, {
          name: form.name,
          price: parseFloat(form.price),
          icon: form.icon,
          categories,
        }, { headers });
      } else {
        await axios.post(`${apiUrl}/products`, {
          name: form.name,
          price: parseFloat(form.price),
          icon: form.icon,
          categories,
        }, { headers });
      }
      setShowModal(false);
      loadProducts();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Supprimer le produit "${product.name}" ?`)) return;
    try {
      await axios.delete(`${apiUrl}/products/${product.id}`, { headers });
      loadProducts();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>📦 Gestion des produits</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Nouveau produit</button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Produit</th>
              <th>Prix</th>
              <th>Catégories</th>
              <th>Date de création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <td style={{ fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', fontSize: '20px' }}>{product.icon}</span>
                  {product.name}
                </td>
                <td>
                  <span className="badge badge-success">{product.price.toFixed(0)} DA</span>
                </td>
                <td style={{ fontSize: '13px' }}>
                  {productCategories(product).map(c => CATEGORIES.find(x => x.value === c)?.label || c).join(', ')}
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                  {product.created_at ? new Date(product.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  }) : '-'}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(product)}>✏️ Modifier</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(product)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editProduct ? `Modifier "${editProduct.name}"` : 'Nouveau produit'}</h2>
            <div className="form-group">
              <label>Icône</label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {ICONS.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setForm({ ...form, icon })}
                    style={{
                      fontSize: '24px',
                      padding: '6px 8px',
                      borderRadius: '8px',
                      border: form.icon === icon ? '2px solid var(--primary)' : '2px solid var(--border)',
                      background: form.icon === icon ? 'var(--primary-light)' : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Nom du produit</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Transat, Sandwich..." />
            </div>
            <div className="form-group">
              <label>Catégories (le produit apparaît dans chaque lieu sélectionné)</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {CATEGORIES.map(c => {
                  const active = form.categories.includes(c.value);
                  return (
                    <button
                      key={c.value}
                      type="button"
                      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => toggleCategory(c.value)}
                      style={{ minWidth: '90px' }}
                    >
                      {active ? '✓ ' : ''}{c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="form-group">
              <label>Prix (DA)</label>
              <input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="Ex: 500" />
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editProduct ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsPage;
